import { BOOKING_STATUSES, BOOKING_STATUS_LABELS, type BookingStatus } from './status-schema.js';
import type { HelenPrimary } from './needs-helen-now.js';

/**
 * ⭐⭐ Board 2812 M3 (plan step 2, R-2812-h, Q243) - THE ONE CHIP A ROW CARRIES, AND
 * ITS ONE NOTE LINE.
 *
 * A Bookings row, a To do card head and a Done row all wear ONE chip: the status
 * word first, then the money folded in ("Confirmed, deposit paid", "Quoted,
 * deposit link sent", "Travelled, paid"). A cash run wears its cash word on EVERY
 * status ("Confirmed, cash to the driver", "Travelled, cash to the driver"), and a
 * cancelled one reads "Cancelled, cash run", so it never reads as money still to
 * collect (board 2812 fix round, item 10). A home's billed run reads its status
 * word alone: homes are invoiced at month end and have no deposit stage. The
 * chip's TONE is the status word's (R1),
 * so nothing about the status model changes; the words after the comma are paint
 * a reader can read aloud, never a second status.
 * ⛔ A TAXI HAND-OFF AND AN UNPAID BALANCE ARE NOTE LINES, NEVER A CHIP (Q-D, Q243):
 * a row keeps one chip.
 * ⛔ EVERY WORD IS A STRING THIS FILE CAN LIST (`FOLDED_CHIP_WORDS`), because a
 * toned chip must carry a word from a registry `conformance.spec.tsx` can derive
 * (its arm (iii)); the list is the registry source, built from the same table the
 * function reads, so the two cannot drift.
 */

/** What the row carries that the chip decides on (the served list row's own keys). */
export type ChipRow = {
  status: string;
  money_kind?: 'web' | 'account' | 'cash';
  payment_status: string | null;
  refund_state: string;
  balance_state: string;
  taxi_passed?: boolean;
  /** ⭐ board 2811's five balance FLAGS (M4, M7, R3), served booleans: each rides the
   *  note line (Q243's class), never a chip (board 2812 M4, the 2811 merge). */
  balance_email_stale?: boolean;
  balance_unpaid_at_departure?: boolean;
  balance_text_failed?: boolean;
  balance_text_unfinished?: boolean;
  balance_text_late?: boolean;
};

/** The money half, after the comma. Each key is one fact the row can carry. */
const MONEY_WORDS = {
  depositPaid: 'deposit paid',
  paid: 'paid',
  depositLinkSent: 'deposit link sent',
  balanceDue: 'balance due',
  balancePaid: 'balance paid',
  refundNotConfirmed: 'refund not confirmed',
  refundFailed: 'refund failed',
  refunded: 'refunded',
  partRefunded: 'part refunded',
  noRefundRecorded: 'no refund recorded',
  cash: 'cash to the driver',
  /** A cancelled cash run: names the run as cash without saying there is money to collect. */
  cashRun: 'cash run',
} as const;
type MoneyWord = keyof typeof MONEY_WORDS;

/** The note line's words (Q243). The five balance flags are board 2811's own words
 *  (`balanceFlag()` on the live line's `Bookings.tsx`, `05d357f1`); "Balance unpaid"
 *  is the booking detail's word too, so the list and the detail say one word. */
export const NOTE_WORDS = {
  taxi: 'Passed to a taxi',
  staleEmail: 'Stale balance email',
  balanceNotPaid: 'Balance unpaid',
  textFailed: 'Balance text failed',
  textUnfinished: 'Balance ask unfinished',
  textLate: 'Balance text late',
} as const;

const isBookingStatus = (v: string): v is BookingStatus => (BOOKING_STATUSES as readonly string[]).includes(v);

/** The status word: the registry's, or the raw value for a status it cannot label
 *  (the same fallback `bookingStatusLabel` keeps, so a future status still shows). */
function statusWord(status: string): string {
  return isBookingStatus(status) ? BOOKING_STATUS_LABELS[status] : status;
}

/**
 * Which money fact the chip folds in, in precedence order: refund news first (it
 * is never entailed by anything else and is the most urgent, T1130), then the
 * balance where it entails the deposit (board 2106 M6's swap: a paid deposit
 * only), then the deposit. `null` = the status word alone.
 */
function moneyFact(r: ChipRow): MoneyWord | null {
  // ⭐ board 2812 fix round (item 10): the cash word on EVERY status, so Helen can tell a
  // past or cancelled run was cash; a cancelled one says "cash run", never money to collect.
  if (r.money_kind === 'cash') return r.status === 'cancelled' ? 'cashRun' : 'cash';
  if (r.money_kind === 'account') return null;
  const paid = r.payment_status === 'deposit_paid' || r.payment_status === 'paid';
  switch (r.refund_state) {
    case 'unconfirmed': return 'refundNotConfirmed';
    case 'failed': return 'refundFailed';
    case 'refunded': return 'refunded';
    case 'part_refunded': return 'partRefunded';
    default: break;
  }
  if (paid && r.status === 'cancelled') return 'noRefundRecorded';
  if (paid && r.balance_state === 'paid') return 'balancePaid';
  if (paid && (r.balance_state === 'asked' || r.balance_state === 'stuck')) return 'balanceDue';
  if (r.payment_status === 'paid') return 'paid';
  if (r.payment_status === 'deposit_paid') return 'depositPaid';
  if (r.payment_status === 'deposit_sent') return 'depositLinkSent';
  if (r.payment_status === 'balance_due') return 'balanceDue';
  return null;
}

/** The row's one chip: its words, and the status whose tone it wears (`null` for a
 *  status the registry cannot tone, which renders untoned).
 *  ⭐ board 2814 M8R1 (R8), ported at board 2812 M5's Keep current: a run passed to a
 *  taxi (and not cancelled) wears NO tone, never Confirmed's green: the run is not
 *  ours to drive while it is passed. Its words stay the status word; "Passed to a
 *  taxi" rides the note line (`noteOf`). */
export function chipWords(r: ChipRow): { words: string; toneStatus: BookingStatus | null } {
  const fact = moneyFact(r);
  const head = statusWord(r.status);
  const passed = r.taxi_passed === true && r.status !== 'cancelled';
  return {
    words: fact === null ? head : `${head}, ${MONEY_WORDS[fact]}`,
    toneStatus: !passed && isBookingStatus(r.status) ? r.status : null,
  };
}

/**
 * ⭐⭐ board 2812 M4 (the 2811 merge) - THE ROW'S ONE NOTE LINE, AND WHETHER IT IS A FLAG.
 * Precedence: board 2811's five balance FLAGS first, in 2811's own order (a stale
 * scheduled email, then the unpaid balance at departure, then a failed, unfinished or
 * late balance text: each asks Helen to act), then a taxi hand-off (it says why there
 * is nothing else to do on the run). A caller's own lower note ("No reply" on a
 * Bookings row) comes only after both, so a flag never hides behind it. A cancelled
 * run keeps its own word and no taxi note (board 2814 M7's rule for the chip it used
 * to ride).
 * ⛔ `flag: true` IS "NEEDS A HUMAN" (board 2811 rework part 2c, Captain item 14):
 * the caller paints the words gold-700 inside the where line (a `.row-flag` span,
 * `Bookings.tsx` `whereLine`; the rule is `app.css` `.ui-row-where > .row-flag`), never a
 * chip, so a row keeps one chip.
 */
export function noteOf(r: ChipRow): { words: string; flag: boolean } | null {
  const flag = balanceFlagWords(r);
  if (flag !== null) return { words: flag, flag: true };
  if (r.taxi_passed === true && r.status !== 'cancelled') return { words: NOTE_WORDS.taxi, flag: false };
  return null;
}

/** The first of board 2811's five balance flags the row carries, in 2811's order. */
function balanceFlagWords(r: ChipRow): string | null {
  if (r.balance_email_stale === true) return NOTE_WORDS.staleEmail;
  if (r.balance_unpaid_at_departure === true) return NOTE_WORDS.balanceNotPaid;
  if (r.balance_text_failed === true) return NOTE_WORDS.textFailed;
  if (r.balance_text_unfinished === true) return NOTE_WORDS.textUnfinished;
  if (r.balance_text_late === true) return NOTE_WORDS.textLate;
  return null;
}

/** The note line's words alone (`noteOf`), or null. */
export function noteWords(r: ChipRow): string | null {
  return noteOf(r)?.words ?? null;
}

/** EVERY toned chip word this file can produce: the registry source
 *  `conformance.spec.tsx` grades toned chips against. */
export const FOLDED_CHIP_WORDS: readonly string[] = BOOKING_STATUSES.flatMap((s) => [
  BOOKING_STATUS_LABELS[s],
  ...Object.values(MONEY_WORDS).map((m) => `${BOOKING_STATUS_LABELS[s]}, ${m}`),
]);

/**
 * ⭐⭐ Board 2812 M3 fix round (Captain's /test-ui on 2814, relayed 2026-10-07) - A ROW
 * IN NEED YOU NOW SAYS THE ACTION, NEVER JUST THE STATE. Its one chip names Helen's
 * next step; outside Need you now the folded status words above stay (R-2812-h).
 * ⛔ AN ACTION CHIP IS NEVER GREEN AND NEVER TONED: the caller renders it in the
 * NEUTRAL box (`<Chip neutral>`), the one the home's ask already wore as "what needs
 * Helen" (no tone, so no new tone and no registry word; a toned chip must carry a
 * status word, `conformance.spec.tsx` arm (iii)). No row that needs Helen shows a
 * green "Confirmed".
 */
export const ACTION_CHIP_WORDS = {
  /** ⭐ board 3020: a home's new request, which needs a price before its Confirm, so
   *  it names the step it is on ("Confirm to book" names a later one). */
  priceThis: 'Price this',
  /** A home's priced request (or one whose Confirm books it). */
  confirmToBook: 'Confirm to book',
  answerChange: 'Answer the change',
  answerCancel: 'Answer the cancel',
  /** A web enquiry. */
  quoteThis: 'Quote this',
  /** A saved, unsent web quote. */
  sendQuote: 'Send the quote',
  /** A home's confirmed request whose booking disagrees with it (DD-11 row 10). */
  checkBooking: 'Check the booking',
  /** A declined or cancelled request whose run is still live (DD-11 row 12). */
  endRun: 'End the run',
  /** A request with no named next step (DD-11 row 6; a link that could not be read). */
  checkRequest: 'Check this request',
  /** ⭐ board 2812 M3 fix round 3 (F3-6): a run in Need an outcome (passed, no outcome
   *  marked) needs Helen, so it names the step too, never a green "Confirmed". */
  markOutcome: 'Mark the outcome',
} as const;

/**
 * The chip a home's request wears in Need you now (and on a Bookings waiting row),
 * from `needsHelenNow`'s primary (the ONE table every count and list on the screen
 * reads, so the chip cannot disagree with the row's own controls) and the request's
 * open ask: "Price this" for a new request (`save-price`, board 3020), "Confirm to
 * book" for a priced one (`confirm`).
 */
export function requestActionChip(
  primary: HelenPrimary | null,
  r: { askState?: string | null; askKind?: string | null },
): string {
  if (r.askState === 'open') {
    return r.askKind === 'cancel' ? ACTION_CHIP_WORDS.answerCancel : ACTION_CHIP_WORDS.answerChange;
  }
  switch (primary) {
    case 'save-price':
      return ACTION_CHIP_WORDS.priceThis;
    case 'confirm':
      return ACTION_CHIP_WORDS.confirmToBook;
    case 'accept':
      return ACTION_CHIP_WORDS.answerChange;
    case 'open-booking':
      return ACTION_CHIP_WORDS.checkBooking;
    case 'end-the-run':
      return ACTION_CHIP_WORDS.endRun;
    case null:
      return ACTION_CHIP_WORDS.checkRequest;
  }
}
